import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FilterListIcon from '@mui/icons-material/FilterList'
import { getJson, getUrl } from '../../providers/data'
import { SheetResponse } from './types'

interface SheetViewerProps {
  prognoseSetId: string
  sheetNames: string[]
}

// Human-readable labels for sheet names
const SHEET_LABELS: Record<string, string> = {
  // Main result sheets
  'pop': 'Bevolking',
  'bevolk': 'Bevolking',
  'geb': 'Geboorte',
  'geboorte': 'Geboorte',
  'ste': 'Sterfte',
  'sterfte': 'Sterfte',
  'imi': 'Vestiging bin.',
  'imu': 'Vestiging bui.',
  'vestig': 'Vestiging',
  'emi': 'Vertrek bin.',
  'emu': 'Vertrek bui.',
  'vertrek': 'Vertrek',
  'hh': 'Huishoudens',
  'huishoud': 'Huishoudens',
  'hsr': 'Huishoudenquote',
  'h_bewoon': 'Huish.bewoning',
  'won': 'Woningen',
  'woning': 'Woningen',
  'wonram': 'Woningraming',
  'gwb': 'Gem. woningbez.',
  'vrl': 'Verloop (lft)',
  'vrl_jr': 'Verloop (jaar)',
  // Gebied-specific
  'popt': 'Bevolking (tot)',
  'popot': 'Bevolking (ongecorr.)',
  'gebo': 'Geboorte (ongecorr.)',
  'mf': 'Mutatiefactor',
  'mp': 'Mutatiefactor (corr.)',
  'fc': 'Vruchtbaarheidscorr.',
  'lpi': 'Leeftijdsprofiel',
  'kk': 'Indicatoren',
  // Meta / config sheets
  'samenvatting': 'Samenvatting',
  'Samenvatting': 'Samenvatting',
  'stuurkaart': 'Stuurkaart',
  'Stuurkaart': 'Stuurkaart',
  'settings': 'Instellingen',
  'Instellingen': 'Instellingen',
  'toelichting': 'Toelichting',
  'nl_kern_data': 'NL kerndata',
  'externe_prognose': 'Externe prognose',
  'buitenlands_migratiesaldo': 'Bui. migratiesaldo',
  // Prognose detail sheets (prstad.*)
  'prstad.pop_pra': 'Bevolking',
  'prstad.geb_pra': 'Geboorte',
  'prstad.hsr_pra': 'Huish.quote',
  'prstad.emk_pra': 'Emigratie',
  'prstad.imp_pra': 'Immigratie',
  'prstad.won_pra': 'Woningen',
  'prstad.bew_pra': 'Bewoning',
  'prstad.hh_pra': 'Huishoudens',
  'prstad.ken_pra': 'Kengetallen',
  'prstad.mut_pra': 'Mutaties',
  'prstad.inv_pra': 'Invoer',
  'prstad.asf_pra': 'Sterftecijfer',
  'prstad.pop_hiam': 'Bevolking (hist)',
  'prstad.geb_hiam': 'Geboorte (hist)',
  'prstad.hsr_hiam': 'Huish.quote (hist)',
  'prstad.ken_hiam': 'Kengetallen (hist)',
  'prstad.hh_hiam': 'Huishoudens (hist)',
  'prstad.asf': 'Sterftecijfer',
  'prstad.emk': 'Emigratiekansen',
  'prstad.imp': 'Immigratie',
  'prstad.stk': 'Stuurkaart',
  'prstad.migcor': 'Migratiecorr.',
}

// Category definitions — order matters for display
interface SheetCategory {
  key: string
  label: string
  match: (name: string) => boolean
}

const CATEGORIES: SheetCategory[] = [
  {
    key: 'samenvatting',
    label: 'Samenvatting',
    match: (n) => ['samenvatting', 'Samenvatting'].includes(n),
  },
  {
    key: 'resultaten',
    label: 'Resultaten',
    match: (n) =>
      [
        'pop', 'bevolk', 'geb', 'geboorte', 'ste', 'sterfte',
        'imi', 'imu', 'vestig', 'emi', 'emu', 'vertrek',
        'hh', 'huishoud', 'hsr', 'h_bewoon',
        'won', 'woning', 'wonram', 'gwb',
        'vrl', 'vrl_jr',
        // Gebied result sheets
        'popt', 'popot', 'gebo', 'mf', 'mp', 'fc', 'lpi', 'kk',
      ].includes(n),
  },
  {
    key: 'invoer',
    label: 'Invoer',
    match: (n) =>
      [
        'settings', 'Instellingen', 'stuurkaart', 'Stuurkaart',
        'nl_kern_data', 'externe_prognose', 'buitenlands_migratiesaldo',
      ].includes(n),
  },
  {
    key: 'berekeningen',
    label: 'Berekeningen',
    match: (n) => n.startsWith('prstad.'),
  },
]

// Sheets where filters should be hidden
const NO_FILTER_SHEETS = ['samenvatting', 'Samenvatting', 'stuurkaart', 'Stuurkaart', 'settings', 'Instellingen', 'toelichting']

const getSheetLabel = (name: string): string => SHEET_LABELS[name] || name

const SheetViewer: React.FC<SheetViewerProps> = ({ prognoseSetId, sheetNames }) => {
  const [selectedSheet, setSelectedSheet] = useState('')
  const [sheetData, setSheetData] = useState<SheetResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Filters
  const [geslacht, setGeslacht] = useState('')
  const [jaarVan, setJaarVan] = useState('')
  const [jaarTot, setJaarTot] = useState('')
  const [leeftijdVan, setLeeftijdVan] = useState('')
  const [leeftijdTot, setLeeftijdTot] = useState('')

  // Group sheets into categories
  const categorizedSheets = useMemo(() => {
    const groups: Record<string, string[]> = {}
    const assigned = new Set<string>()

    for (const cat of CATEGORIES) {
      const matching = sheetNames.filter((s) => cat.match(s))
      if (matching.length > 0) {
        groups[cat.key] = matching
        matching.forEach((s) => assigned.add(s))
      }
    }

    // Anything not categorized goes into "Overig"
    const remaining = sheetNames.filter((s) => !assigned.has(s))
    if (remaining.length > 0) {
      groups.overig = remaining
    }

    return groups
  }, [sheetNames])

  // Available category keys (preserving order)
  const availableCategories = useMemo(() => {
    const ordered = CATEGORIES.map((c) => c.key).filter((k) => categorizedSheets[k])
    if (categorizedSheets.overig) ordered.push('overig')
    return ordered
  }, [categorizedSheets])

  const categoryLabels: Record<string, string> = {
    samenvatting: 'Samenvatting',
    resultaten: 'Resultaten',
    invoer: 'Invoer',
    berekeningen: 'Berekeningen',
    overig: 'Overig',
  }

  // Initialize: pick default category and sheet
  useEffect(() => {
    if (availableCategories.length > 0 && !selectedCategory) {
      // Prefer 'samenvatting', then 'resultaten', then first available
      const preferred = ['samenvatting', 'resultaten']
      const defaultCat = preferred.find((c) => availableCategories.includes(c)) || availableCategories[0]
      setSelectedCategory(defaultCat)
    }
  }, [availableCategories, selectedCategory])

  // When category changes, select first sheet in that category
  useEffect(() => {
    if (selectedCategory && categorizedSheets[selectedCategory]) {
      const sheets = categorizedSheets[selectedCategory]
      if (sheets.length > 0 && !sheets.includes(selectedSheet)) {
        setSelectedSheet(sheets[0])
      }
    }
  }, [selectedCategory, categorizedSheets, selectedSheet])

  const fetchSheet = useCallback(async (sheetName: string, filters?: {
    geslacht?: string
    jaarVan?: string
    jaarTot?: string
    leeftijdVan?: string
    leeftijdTot?: string
  }) => {
    if (!prognoseSetId || !sheetName) return
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters?.geslacht) params.set('geslacht', filters.geslacht)
      if (filters?.jaarVan) params.set('jaar_van', filters.jaarVan)
      if (filters?.jaarTot) params.set('jaar_tot', filters.jaarTot)
      if (filters?.leeftijdVan) params.set('leeftijd_van', filters.leeftijdVan)
      if (filters?.leeftijdTot) params.set('leeftijd_tot', filters.leeftijdTot)

      const qs = params.toString()
      const result = await getJson<SheetResponse>(
        `${getUrl()}/prognose_sets/${prognoseSetId}/resultaat/${sheetName}${qs ? `?${qs}` : ''}`
      )
      setSheetData(result)
    } catch (err) {
      console.error('Failed to load sheet:', err)
      setSheetData(null)
    } finally {
      setLoading(false)
    }
  }, [prognoseSetId])

  useEffect(() => {
    if (selectedSheet) {
      void fetchSheet(selectedSheet)
    }
  }, [selectedSheet, fetchSheet])

  const handleApplyFilters = () => {
    void fetchSheet(selectedSheet, { geslacht, jaarVan, jaarTot, leeftijdVan, leeftijdTot })
  }

  const handleResetFilters = () => {
    setGeslacht('')
    setJaarVan('')
    setJaarTot('')
    setLeeftijdVan('')
    setLeeftijdTot('')
    void fetchSheet(selectedSheet)
    setFiltersOpen(false)
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedSheet(newValue)
  }

  const showFilters = !NO_FILTER_SHEETS.includes(selectedSheet)
  const hasGeslachtColumn = sheetData?.columns?.includes('geslacht')
  const hasActiveFilters = !!(geslacht || jaarVan || jaarTot || leeftijdVan || leeftijdTot)
  const currentSheets = categorizedSheets[selectedCategory] || []

  // Compute totals row for numeric columns
  const totalsRow = sheetData?.data && sheetData.data.length > 0
    ? sheetData.columns?.reduce((acc, col) => {
      const isYearCol = /^\d{4}$/.test(col)
      const isValueCol = col === 'waarde' || col === 'aantal'
      if (isYearCol || isValueCol) {
        acc[col] = sheetData.data!.reduce((sum, row) => {
          const val = row[col]
          return sum + (typeof val === 'number' ? val : 0)
        }, 0)
      }
      return acc
    }, {} as Record<string, number>)
    : null

  return (
    <>
      {/* Category chips + filter toggle */}
      <Paper sx={{ mb: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          {availableCategories.map((catKey) => (
            <Chip
              key={catKey}
              label={categoryLabels[catKey] || catKey}
              size="small"
              variant={selectedCategory === catKey ? 'filled' : 'outlined'}
              color={selectedCategory === catKey ? 'primary' : 'default'}
              onClick={() => setSelectedCategory(catKey)}
              sx={{ fontWeight: selectedCategory === catKey ? 600 : 400 }}
            />
          ))}

          <Box sx={{ flex: 1 }} />

          {showFilters && (
            <Tooltip title={hasActiveFilters ? 'Filters actief' : 'Filters'}>
              <IconButton
                size="small"
                onClick={() => setFiltersOpen(!filtersOpen)}
                color={hasActiveFilters ? 'primary' : 'default'}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {sheetData?.total != null && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
              {sheetData.total} rijen
            </Typography>
          )}
        </Box>

        {/* Sheet tabs within selected category */}
        {currentSheets.length > 1 && (
          <Tabs
            value={currentSheets.includes(selectedSheet) ? selectedSheet : false}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              'minHeight': 36,
              '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' },
            }}
          >
            {currentSheets.map((s) => (
              <Tab key={s} value={s} label={getSheetLabel(s)} />
            ))}
          </Tabs>
        )}

        {/* Collapsible filters */}
        <Collapse in={filtersOpen && showFilters}>
          <Box sx={{
            display: 'flex',
            gap: 1.5,
            px: 1.5,
            py: 1,
            alignItems: 'center',
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}>
            {hasGeslachtColumn && (
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Geslacht</InputLabel>
                <Select
                  value={geslacht}
                  label="Geslacht"
                  onChange={(e) => setGeslacht(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="m">Man</MenuItem>
                  <MenuItem value="v">Vrouw</MenuItem>
                  <MenuItem value="M">Man (M)</MenuItem>
                  <MenuItem value="V">Vrouw (V)</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              label="Jaar van"
              type="number"
              value={jaarVan}
              onChange={(e) => setJaarVan(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              size="small"
              label="Jaar tot"
              type="number"
              value={jaarTot}
              onChange={(e) => setJaarTot(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              size="small"
              label="Leeftijd van"
              type="number"
              value={leeftijdVan}
              onChange={(e) => setLeeftijdVan(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label="Leeftijd tot"
              type="number"
              value={leeftijdTot}
              onChange={(e) => setLeeftijdTot(e.target.value)}
              sx={{ width: 110 }}
            />
            <Button size="small" variant="contained" onClick={handleApplyFilters} sx={{ whiteSpace: 'nowrap' }}>
              Toepassen
            </Button>
            <Button size="small" startIcon={<RestartAltIcon />} onClick={handleResetFilters}>
              Reset
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Data table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : sheetData?.data ? (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 340px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {sheetData.columns?.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      py: 0.75,
                      minWidth: /^\d{4}$/.test(col) ? 72 : undefined,
                      backgroundColor: 'background.paper',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sheetData.data.map((row, idx) => (
                <TableRow key={idx} hover sx={{ '& td': { py: 0.4, fontSize: '0.8125rem' } }}>
                  {sheetData.columns?.map((col) => (
                    <TableCell key={col}>
                      {row[col] != null
                        ? typeof row[col] === 'number'
                          ? (row[col] as number).toLocaleString('nl-NL', {
                            maximumFractionDigits: 4,
                          })
                          : String(row[col])
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {/* Totals row */}
              {totalsRow && Object.keys(totalsRow).length > 0 && (
                <TableRow sx={{ 'backgroundColor': 'action.hover', '& td': { py: 0.5 } }}>
                  {sheetData.columns?.map((col, idx) => (
                    <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      {idx === 0 && !totalsRow[col] ? 'Totaal' : ''}
                      {totalsRow[col] != null
                        ? (totalsRow[col]).toLocaleString('nl-NL', {
                          maximumFractionDigits: 2,
                        })
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : sheetData?.error ? (
        <Paper sx={{ p: 2 }}>
          <Typography color="error">{sheetData.error}</Typography>
          {sheetData.available_sheets && (
            <Typography variant="body2">
              Beschikbare sheets: {sheetData.available_sheets.join(', ')}
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography color="textSecondary">Geen data beschikbaar</Typography>
        </Paper>
      )}
    </>
  )
}

export default SheetViewer
